import React, {useState, useEffect, useContext, useRef} from 'react';
import {toast} from 'react-toastify';
import {useHistory, useParams} from 'react-router-dom';
import SavedSearchesList from './SavedSearchList';
import SavedSearchesView from './SavedSearchView';
import ListFilters from './SavedSearchListFilters';
import {SavedSearchContext} from 'contexts/SavedSearchContext';
import {ErrorContext} from 'contexts/ErrorContext';
import {assignAlertToUsers} from 'utils/api';
import {getEntities, updateEntityNewsSorting} from 'utils/api/entitiesApi';
import {
    getSavedSearches, getSavedSearchResults,
    getSavedSearchById, editSavedSearch, createSavedSearch, getSearchResultsByKeyword,
} from 'utils/api/savedSearchesApi';
import {getUsers} from 'utils/api/usersApi';
import {getRandomColor} from 'utils/other';
import Loader from 'components/Loader';
import {connect} from 'react-redux';
import NoEntitiesPage from './NoEntitiesPage';
import {textTruncateByLength} from 'utils/other';
import {keys, pickBy} from 'lodash';
import './style.scss';

const CombinedHomePage = ({auth}) => {
    let {activeIdFromUrl} = useParams();
    let history = useHistory();

    let {handleErrors} = useContext(ErrorContext);
    let {
        activeSavedSearch, setActiveSavedSearch, setSelectedEntities, resetSavedSearchForm,
        savedSearchSentimentFilters, savedSearchSocialFilters, setSavedSearchSentimentFilters,
        setSavedSearchSocialFilters, selectedEntities, sliderVisible, setSliderVisible, setSavedSearchSocialCount,
        savedSearchPeriodFilter, activeSavedSearchSort, setSavedSearchSentimentCount,
    } = useContext(SavedSearchContext);

    const [savedSearchesLoading, setSavedSearchesLoading] = useState(true);
    const [savedSearches, setSavedSearches] = useState([]);
    const [entitiesLoading, setEntitiesLoading] = useState(true);
    const [fullEntitiesList, setFullEntitiesList] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [usersList, setUsersList] = useState([]);
    const [assigningSavedSearch, setAssigningSavedSearch] = useState(false);
    const [initialEntityCheck, setInitialEntityCheck] = useState(true);
    const [showEmptyEntitiesScreen, setShowEmptyEntitiesScreen] = useState(false);
    const [error, setError] = useState(null);
    const [savingSSData, setSavingSSData] = useState(false);
    const [titleValue, setTitleValue] = useState('');
    const [tempTitleValue, setTempTitleValue] = useState('');
    const [keywordSearchValue, setKeywordSearchValue] = useState('');
    const [inTitleOnly, setInTitleOnly] = useState(false);
    const [totalCount, setTotalCount] = useState(null);
    const [searchResults, setSearchResults] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [populatingExistingFields, setPopulatingExistingFields] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [updatingSSFilters, setUpdatingSSFilters] = useState(false);
    const [updatingSSInfo, setUpdatingSSInfo] = useState(false);

    useEffect(() => {
        if(auth.permissions) {
            if(!auth.permissions.includes('search')) {
                history.push('/');
            } else {
                auth.permissions.includes('entity') && fetchEntities();
                auth.permissions.includes('search') && fetchSavedSearches();
                fetchUsers();
            }
        }
    }, []);


    useEffect(() => {
        if(activeSavedSearch) {
            (previousActiveSavedSearch === null ||
                (previousActiveSavedSearch && (activeSavedSearch.id !== previousActiveSavedSearch.id))) && getSSById(activeSavedSearch.id);
        }
    }, [activeSavedSearch]);

    useEffect(() => {
        if(!sliderVisible) return;
        if(activeSavedSearch) {
            submitSSData(null, null, true);
        } else {
            getSearchResultsByKeyword(true);
        }
    }, [savedSearchPeriodFilter]);

    const usePrevious = (value) => {
        const ref = useRef();
        useEffect(() => {
            ref.current = value;
        });
        return ref.current;
    };
    const previousActiveSavedSearch = usePrevious(activeSavedSearch);

    const toggleEditMode = () => setEditMode(prevState => !prevState);

    const toggleInTitleOnly = () => {
        if(!activeSavedSearch) {
            setInTitleOnly(prevState => !prevState);
        } else {
            submitSSData(null, !inTitleOnly);
        }
    };

    const fetchEntities = async() => {
        try {
            !entitiesLoading && setEntitiesLoading(true);
            let entitiesData = await getEntities(true);
            if(Array.isArray(entitiesData.content.result) && entitiesData.content.result.length === 0) {
                setShowEmptyEntitiesScreen(true);
                setInitialEntityCheck(false);
                return;
            }
            entitiesData.content.result.forEach(item => item.color = getRandomColor());
            entitiesData.content.result.sort((a, b) => (a.title && b.title) && a.title.localeCompare(b.title));
            setFullEntitiesList(entitiesData.content.result);
            setSelectedEntities(entitiesData.content.result.map(ent => ent.id));
            setInitialEntityCheck(false);
            setEntitiesLoading(false);
        } catch(err) {
            handleErrors(err);
            setEntitiesLoading(false);
        }
    };

    const fetchSavedSearches = async(newlyCreatedSavedSearchData, updating) => {
        try {
            !savedSearchesLoading && !updating && setSavedSearchesLoading(true);
            let savedSearchesList = await getSavedSearches();
            setSavedSearches(savedSearchesList.content.result);
            setSavedSearchesLoading(false);
            if(activeSavedSearch || newlyCreatedSavedSearchData) {
                let ssIdToFind = newlyCreatedSavedSearchData ? newlyCreatedSavedSearchData.id : activeSavedSearch.id;
                let foundSearch = savedSearchesList.content.result.find(item => item.id === ssIdToFind);
                (foundSearch || updating) ? setActiveSavedSearch(foundSearch) : resetSavedSearchForm();
            }
        } catch(err) {
            console.log(err);
            handleErrors(err);
            setSavedSearchesLoading(false);
        }
    };

    const fetchUsers = async() => {
        try {
            !usersLoading && setUsersLoading(true);
            let usersData = await getUsers();
            setUsersList(usersData.content.result);
            setUsersLoading(false);
        } catch(err) {
            console.log(err);
            if(err.message && err.message.length < 150) {
                toast.error(err.message);
            }
            setUsersLoading(false);
        }
    };

    const handleAssignAlert = async(sourceId, selectedUsers, type) => {
        try {
            setAssigningSavedSearch(true);
            const assignment = await assignAlertToUsers(sourceId, selectedUsers, type);
            fetchSavedSearches();
            setAssigningSavedSearch(false);
        } catch(err) {
            handleErrors(err);
            setAssigningSavedSearch(false);
        }
    };

    const getSearchResultsByKeyword = async(updating, newSourceFilters, newSentimentFilters, newSelectedEntityArr) => {
        try {
            let sourceObjectToApply = newSourceFilters ? newSourceFilters : savedSearchSocialFilters;
            let sentimentObjectToApply = newSentimentFilters ? newSentimentFilters : savedSearchSentimentFilters;

            setError(null);
            updating ? setUpdatingSSFilters(true) : setLoading(true);
            let excludedSources = [];
            let allowedSentiments = [];
            for(const property in sourceObjectToApply) {
                if(!sourceObjectToApply[property]) {
                    excludedSources.push(property);
                }
            }
            for(const property in sentimentObjectToApply) {
                if(sentimentObjectToApply[property]) {
                    property === 'negative' ? allowedSentiments.push(-1) :
                        property === 'neutral' ? allowedSentiments.push(0) :
                            allowedSentiments.push(1);
                }
            }

            const existingEntities = fullEntitiesList.map(ent => ent.id);
            const newSelectedEntities = newSelectedEntityArr ?
                newSelectedEntityArr.filter((item) => existingEntities.includes(item)) :
                selectedEntities.filter((item) => existingEntities.includes(item));

            let params = {
                keyword: inTitleOnly ? 'intitle:' + keywordSearchValue : keywordSearchValue,
                entities: newSelectedEntities.length === fullEntitiesList.length ? [] : newSelectedEntities,
                typesNotIn: excludedSources,
                sentiments: allowedSentiments,
                period: savedSearchPeriodFilter,
            };

            let searchData = await getSavedSearchResults(params);
            let resultStats = searchData.content.stats;
            applySourceCount(resultStats.sources);
            applySentimentCount(resultStats.sentiments);
            let count = resultStats.total || 0;
            setTotalCount(count);
            let results = searchData.content.result && searchData.content.result.length ? searchData.content.result : [];
            !titleValue && setTempTitleValue(textTruncateByLength(keywordSearchValue, 50));
            setSearchResults(results);
            setSliderVisible(true);
            updating ? setUpdatingSSFilters(false) : setLoading(false);
        } catch(err) {
            updating ? setUpdatingSSFilters(false) : setLoading(false);
            if(err.message && err.message.length < 150) {
                toast.error(err.message);
            }
        }
    };

    const submitSSData = async(newTitleValue, newInTitleValue, updatingFromFilter, newSourceFilters, newSentimentFilters, newSelectedEntityArr) => {
        try {
            setError(null);
            if(selectedEntities.length === 0 && (!newSelectedEntityArr || newSelectedEntityArr.length === 0)) {
                return;
            }
            updatingFromFilter && setUpdatingSSFilters(true);
            setUpdatingSSInfo(true);
            !updatingFromFilter && setSubmitting(true);
            setPopulatingExistingFields(true);
            let sourceObjectToApply = newSourceFilters ? newSourceFilters : savedSearchSocialFilters;
            let sentimentObjectToApply = newSentimentFilters ? newSentimentFilters : savedSearchSentimentFilters;

            let excludedSources = [];
            let allowedSentiments = [];
            const existingEntities = fullEntitiesList.map(ent => ent.id);
            const newSelectedEntities = newSelectedEntityArr ?
                newSelectedEntityArr.filter((item) => existingEntities.includes(item)) :
                selectedEntities.filter((item) => existingEntities.includes(item));
            for(const property in sourceObjectToApply) {
                if(!sourceObjectToApply[property]) {
                    excludedSources.push(property);
                }
            }
            for(const property in sentimentObjectToApply) {
                if(sentimentObjectToApply[property]) {
                    property === 'negative' ? allowedSentiments.push(-1) :
                        property === 'neutral' ? allowedSentiments.push(0) :
                            allowedSentiments.push(1);
                }
            }
            let titleParam = (activeSavedSearch && newTitleValue) ?
                newTitleValue : (titleValue ? titleValue : tempTitleValue);
            let inTitle = !(newInTitleValue === null || newInTitleValue === undefined) ? newInTitleValue : inTitleOnly;
            const params = {
                keyword: inTitle ? `intitle:${keywordSearchValue}` : keywordSearchValue,
                title: titleParam,
                entities: newSelectedEntities.length === fullEntitiesList.length ? [] : newSelectedEntities,
                typesNotIn: excludedSources,
                sentiments: allowedSentiments,
                sorting: parseInt(activeSavedSearchSort),
                daysBefore: savedSearchPeriodFilter,
            };

            let submitData;
            if(activeSavedSearch) {
                submitData = await editSavedSearch(params, activeSavedSearch.id);
                activeSavedSearch.sourceName !== titleParam && fetchSavedSearches(activeSavedSearch, true);

                let results = submitData.content.result ? submitData.content.result : [];
                let resultStats = submitData.content.stats;
                applySourceCount(resultStats.sources);
                applySentimentCount(resultStats.sentiments);
                let count = resultStats.total || 0;
                setSearchResults(results);
                setTotalCount(count);
                populateSSValues(submitData.content.searchData);
                // setSliderVisible(true);

            } else {
                submitData = await createSavedSearch(params);
                fetchSavedSearches(submitData.content.result[0]);
                toast.success('Search successfully created');
            }
            setSubmitting(false);
            setUpdatingSSFilters(false);
            setUpdatingSSInfo(false);
        } catch(err) {
            console.log(err);
            setSubmitting(false);
            setUpdatingSSInfo(false);
            handleErrors(err, setError);
        }
    };

    const getSSById = async(savedSearchId, updating) => {
        if(!savedSearchId) return;
        try {
            updating ? setUpdatingSSFilters(true) : setLoading(true);
            let ssData = await getSavedSearchById(savedSearchId, activeSavedSearchSort, savedSearchPeriodFilter);
            let results = ssData.content.result ? ssData.content.result : [];
            let resultStats = ssData.content.stats;
            applySourceCount(resultStats.sources);
            applySentimentCount(resultStats.sentiments);
            let count = resultStats.total || 0;
            setSearchResults(results);
            setTotalCount(count);
            !updating && populateSSValues(ssData.content.searchData);
            setSliderVisible(true);
            updating ? setUpdatingSSFilters(false) : setLoading(false);
        } catch(err) {
            updating ? setUpdatingSSFilters(false) : setLoading(false);
            console.log(err);
            if(err.message && err.message.length < 150) {
                toast.error(err.message);
            }
        }
    };

    const populateSSValues = (savedSearch) => {
        let keywordVal = savedSearch.keyword;
        let inTitleVal = false;
        if(savedSearch.keyword.slice(0, 8) === 'intitle:') {
            keywordVal = savedSearch.keyword.slice(8, savedSearch.keyword.length);
            inTitleVal = true;
        }
        setKeywordSearchValue(decodeURIComponent(keywordVal));
        setInTitleOnly(inTitleVal);
        setTitleValue(savedSearch.name);
        setTempTitleValue(savedSearch.name);
        let socialFiltersObj = {
            'twitter': true,
            'facebook': true,
            'youtube': true,
            'instagram': true,
            'rss': true,
            'web': true,
            'google_ozmosys': true,
        };
        let sentimentFiltersObj = {
            'negative': true,
            'neutral': true,
            'positive': true,
        };
        if(savedSearch.settings.typesNotIn && savedSearch.settings.typesNotIn.length > 0) {
            savedSearch.settings.typesNotIn.forEach(source => {
                socialFiltersObj[source] = false;
            });
        }

        if(savedSearch.settings.sentiments && savedSearch.settings.sentiments.length > 0) {
            sentimentFiltersObj['negative'] = savedSearch.settings.sentiments.includes(-1);
            sentimentFiltersObj['neutral'] = savedSearch.settings.sentiments.includes(0);
            sentimentFiltersObj['positive'] = savedSearch.settings.sentiments.includes(1);
        }
        setSavedSearchSocialFilters(socialFiltersObj);
        setSavedSearchSentimentFilters(sentimentFiltersObj);
        if(savedSearch.settings.entities.length === 0) {
            let allEntityIds = fullEntitiesList.map(item => item.id);
            setSelectedEntities(allEntityIds);
        } else {
            setSelectedEntities(savedSearch.settings.entities);
        }
        setPopulatingExistingFields(false);
    };

    const resetSearchValues = () => {
        setKeywordSearchValue('');
        setInTitleOnly(false);
        setTitleValue('');
        setTempTitleValue('');
        setSearchResults(null);
        setSelectedEntities(fullEntitiesList.map(ent => ent.id));
        resetSavedSearchForm();
    };

    const handleTitleInput = (val) => {
        setTitleValue(val);
        setTempTitleValue(val);
    };

    const handleTitleInputBlur = (e) => {
        if(!titleValue) {
            setTempTitleValue(textTruncateByLength(keywordSearchValue, 50));
        }
        activeSavedSearch && submitSSData(e.target.value);
        setEditMode(false);
    };

    const toggleSentimentFilters = async(sentiment) => {
        if(updatingSSInfo || updatingSSFilters || loading) return;
        let sentimentFiltersClone = JSON.parse(JSON.stringify(savedSearchSentimentFilters));
        sentimentFiltersClone[sentiment] = !sentimentFiltersClone[sentiment];
        const trueSentiments = keys(pickBy(sentimentFiltersClone));
        if(trueSentiments.length === 0) {
            toast.error('At least one sentiment value must be selected');
            return;
        }
        setSavedSearchSentimentFilters(sentimentFiltersClone);
        if(selectedEntities.length === 0) return;
        if(!activeSavedSearch && searchResults) {
            await getSearchResultsByKeyword(true, null, sentimentFiltersClone);
        } else if(activeSavedSearch) {
            await submitSSData(null, null, true, null, sentimentFiltersClone);
            // await getSSById(activeSavedSearch.id, true);
        }
    };

    const toggleSocialFilters = async(source) => {
        if(updatingSSInfo || updatingSSFilters || loading) return;
        let socialFiltersClone = JSON.parse(JSON.stringify(savedSearchSocialFilters));
        socialFiltersClone[source] = !socialFiltersClone[source];
        setSavedSearchSocialFilters(socialFiltersClone);
        if(selectedEntities.length === 0) return;
        if(!activeSavedSearch && searchResults) {
            await getSearchResultsByKeyword(true, socialFiltersClone, null);
        } else if(activeSavedSearch) {
            await submitSSData(null, null, true, socialFiltersClone, null);
            // await getSSById(activeSavedSearch.id, true);
        }
    };

    const handleEntitySelect = async(entityId, toggleAll) => {
        if(updatingSSInfo || updatingSSFilters || loading) return;
        let activeEntitiesArr = toggleAll ? [] : [...selectedEntities];

        if(toggleAll) {
            selectedEntities.length === 0 && fullEntitiesList.forEach(entity => activeEntitiesArr.push(entity.id));
        } else {
            if(activeEntitiesArr.indexOf(entityId) > -1) {
                activeEntitiesArr.splice(activeEntitiesArr.indexOf(entityId), 1);
            } else {
                activeEntitiesArr.push(entityId);
            }
        }
        setSelectedEntities(activeEntitiesArr);
        if(toggleAll && selectedEntities.length !== 0 || (!toggleAll && activeEntitiesArr.length === 0)) {
            setSearchResults([]);
            setTotalCount(null);
            return;
        }
        if(!activeSavedSearch && searchResults) {
            await getSearchResultsByKeyword(true, null, null, activeEntitiesArr);
        } else if(activeSavedSearch) {
            await submitSSData(null, null, true, null, null, activeEntitiesArr);
            // await getSSById(activeSavedSearch.id, true);
        }
    };


    const applySourceCount = (countData) => {
        let sourceCountObj = {
            twitter: 0,
            facebook: 0,
            youtube: 0,
            instagram: 0,
            rss: 0,
            web: 0,
            google_ozmosys: 0,
        };
        for(const property in countData) {
            if(sourceCountObj.hasOwnProperty(property)) {
                sourceCountObj[property] = countData[property];
            }
        }
        setSavedSearchSocialCount(sourceCountObj);
    };

    const applySentimentCount = (countData) => {
        let sentimentCountObj = {
            positive: 0,
            neutral: 0,
            negative: 0,
        };
        for(const property in countData) {
            if(sentimentCountObj.hasOwnProperty(property.toLowerCase())) {
                sentimentCountObj[property.toLowerCase()] = countData[property];
            }
        }
        setSavedSearchSentimentCount(sentimentCountObj);
    };

    if(initialEntityCheck) return (
        <div className="container_fullsize-centered">
            <Loader/>
        </div>
    );
    if(showEmptyEntitiesScreen) return (
        <NoEntitiesPage/>
    );
    return (
        <div className="d-flex align-items-center h-100 flex-grow-1">
            <div className="home-page__container">
                <div className="home-page__body">
                    <div className="home-page__left">
                        <div className="full-page-wrapper">
                            <SavedSearchesList
                                savedSearches={savedSearches}
                                fullEntitiesList={fullEntitiesList}
                                usersList={usersList}
                                handleAssignAlert={handleAssignAlert}
                                assigningSavedSearch={assigningSavedSearch}
                                resetSearchValues={resetSearchValues}
                                refreshSavedSearches={fetchSavedSearches}
                            />
                        </div>
                    </div>
                    <div className="home-page__center">
                        <div className="full-page-wrapper">
                            <SavedSearchesView
                                fullEntitiesList={fullEntitiesList}
                                refreshSavedSearches={fetchSavedSearches}
                                inTitleOnly={inTitleOnly}
                                setInTitleOnly={setInTitleOnly}
                                searchResults={searchResults}
                                keywordSearchValue={keywordSearchValue}
                                setKeywordSearchValue={setKeywordSearchValue}
                                titleValue={titleValue}
                                tempTitleValue={tempTitleValue}
                                submitSSData={submitSSData}
                                setEditMode={setEditMode}
                                handleTitleInput={handleTitleInput}
                                handleTitleInputBlur={handleTitleInputBlur}
                                toggleInTitleOnly={toggleInTitleOnly}
                                editMode={editMode}
                                toggleEditMode={toggleEditMode}
                                savingSSData={savingSSData}
                                totalCount={totalCount}
                                resetSearchValues={resetSearchValues}
                                selectedEntities={selectedEntities}
                                getSearchResultsByKeyword={getSearchResultsByKeyword}
                                loading={loading}
                                updatingSSFilters={updatingSSFilters}
                                updatingSSInfo={updatingSSInfo}
                                submitting={submitting}
                            />
                        </div>
                    </div>
                    <div className="home-page__right">
                        <ListFilters
                            entitiesLoading={entitiesLoading}
                            updating={updatingSSFilters || updatingSSInfo || loading}
                            entities={fullEntitiesList}
                            toggleSentimentFilters={toggleSentimentFilters}
                            toggleSocialFilters={toggleSocialFilters}
                            handleEntitySelect={handleEntitySelect}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateFromProps = ({auth}) => ({
    auth,
});

export default connect(mapStateFromProps, null)(CombinedHomePage);
